import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Routes from 'Routes';
import { useTranslation } from 'react-i18next';
import { DEFAULT_THEME_COLOR } from 'appenv';
import Avatar from '@material-ui/core/Avatar';
import { selectAllSpeakers } from 'models/webinars';
import { selectLocalizedEventName } from 'models/virtual_event';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocale } from 'models/localization';
import CustomHelmet from 'components/CustomHelmet';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';

const RootWrapper = styled.div`
  height: 100%;
  overflow: auto;
`;

const Wrapper = styled.div`
  max-width: 984px;
  margin: 0 auto;
  padding: 8px 20px;
`;

const Title = styled.h2`
  font-weight: bold;
  font-size: 18px;
  color: #484848;
  margin: 16px 0;
`;

const Description = styled.div`
  font-size: 16px;
  color: #484848;
`;

const ListContainer = styled.div`
  display: flex;
  @media (min-width: 984px) {
    flex-wrap: wrap;
  }
  @media (max-width: 983px) {
    flex-direction: column;
  }
`;

const RootContainer = styled.div`
  flex: 1 0 auto;
  display: flex;
  align-items: flex-start;
  margin: 20px 8px;
  cursor: pointer;
  overflow: hidden;
  width: 450px;

  @media (max-width: 983px) {
    width: 100%;
  }
`;

const ProfileContainer = styled.div`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  flex: 1 1 auto;
`;

const NameContainer = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  color: ${({ color }) => color};
  word-break: break-word;
`;

const CompanyTitleContainer = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  color: #484848;
  padding-top: 8px;
  word-break: break-word;
`;

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const SpeakersPageContainer = () => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const eventName = useSelector(selectLocalizedEventName);

  const speakerList = useSelector(selectAllSpeakers());
  const { themeColor, speakers, pageNames } = useSelector((s) => s.eventConfigs);
  const pageTitle = pageNames.speakers?.[locale] || t('page.speakers', 'Speaker Page');

  return (
    <ScrollableContainerWrapper WrapperContainer={RootWrapper}>
      <Wrapper>
        <CustomHelmet>
          <meta charSet="utf-8" />
          <title>{`${pageTitle} | ${eventName}`}</title>
          <meta name="description" content={`${pageTitle} page of ${eventName}.`} />
        </CustomHelmet>
        <Title>{t('speakers.about_speakers', 'About Speakers')}</Title>
        <Description>{findAvailableLocale(speakers?.description, locale)}</Description>
        <ListContainer>
          {
            speakerList.map(({
              webinarId, id, avatar, translations,
            }) => {
              const {
                name, company, title,
              } = findAvailableLocale(translations, locale);
              return (
                <StyledLink key={id} to={Routes.webinarSpeaker(webinarId, id)}>
                  <RootContainer>
                    <StyledAvatar alt={name} src={avatar} />
                    <ProfileContainer>
                      {name && <NameContainer color={themeColor || DEFAULT_THEME_COLOR}>{ name }</NameContainer>}
                      {company && <CompanyTitleContainer>{ company }</CompanyTitleContainer>}
                      {title && <CompanyTitleContainer>{ title }</CompanyTitleContainer>}
                    </ProfileContainer>
                  </RootContainer>
                </StyledLink>
              );
            })
          }
        </ListContainer>
        <FloatingFooter hashState={Routes.speakers} />
      </Wrapper>
    </ScrollableContainerWrapper>
  );
};

export default SpeakersPageContainer;
